import { useCallback, useEffect, useMemo, useState } from "react";
import { Form, useToast } from "@ir/ui";
import {
  DeleteAllMasterDataDeductionTypesDTO,
  DeleteMasterDataDeductionTypesDTO,
  GetMasterDataDeductionCategoriesDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataDeductionType } from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataDeductionTypeUseCase,
  DeleteAllMasterDataDeductionTypesUseCase,
  DeleteMasterDataDeductionTypesUseCase,
  GetMasterDataDeductionCategoriesUseCase,
  UpdateMasterDataDeductionTypeUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataDeductionCategoryRepository, MasterDataDeductionTypeRepository } from "@src/infrastructure/repositories/masterData";
import { useUserPermissions } from "@src/presentation/hooks/permissions";
import { useMasterDataDeductionTypes } from "@src/presentation/providers/MasterDataDeductionTypesDataProvider";
import { invalidateMasterDataListCacheScopes } from "@src/presentation/providers/masterData/masterDataListCache";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataDeductionTypeAddEditForm,
  type DeductionCategorySelectOption,
  type DeductionTypeAddEditFormValues,
} from "../utils/MasterDataDeductionTypeAddEditForm";
import {
  MasterDataDeductionTypesTable,
  type MasterDataDeductionSubTypeTarget,
} from "../utils/MasterDataDeductionTypesTable";
import { MasterDataDeductionSubTypesModal } from "../utils/MasterDataDeductionSubTypesModal";
import {
  MasterDataDeductionTypeDeleteConfirmModal,
  MasterDataDeductionTypeDeleteToolbar,
  type MasterDataDeductionTypeDeleteMode,
} from "../utils/MasterDataDeductionTypeDeleteActions";
import { MasterDataJsonPreviewModal } from "../utils/MasterDataJsonPreviewModal";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import {
  applyDeductionTypeSaveError,
  getDeductionTypeFormInitialValues,
  getMasterDataDeleteErrorMessage,
  notifyDeductionTypeDeleteResult,
  saveMasterDataDeductionType,
} from "../utils/deductionTypeForm.shared";
import { formatMasterDataRecordCount, useMasterDataAddEditModal } from "../utils/useMasterDataAddEditModal";

export const DEDUCTION_TYPES_PAGE_SIZE = 100;

export const MasterDataDeductionTypesPage = () => {
  const {
    deductionTypes,
    loading,
    search,
    setSearch,
    total,
    page,
    setPage,
    refetch,
  } = useMasterDataDeductionTypes();
  const { success: successToast, error: errorToast } = useToast();
  const { hasAnyPermission } = useUserPermissions();
  const [form] = Form.useForm<DeductionTypeAddEditFormValues>();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleteMode, setDeleteMode] =
    useState<MasterDataDeductionTypeDeleteMode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [subTypeTarget, setSubTypeTarget] =
    useState<MasterDataDeductionSubTypeTarget | null>(null);
  const [metadataPreview, setMetadataPreview] =
    useState<MasterDataDeductionType | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<
    DeductionCategorySelectOption[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const canDelete = hasAnyPermission([Permissions.MASTER_DATA_DELETE]);

  const {
    modalMode,
    editingEntity,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataDeductionType>();

  const repository = useMemo(
    () => new MasterDataDeductionTypeRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataDeductionTypeUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataDeductionTypeUseCase(repository),
    [repository],
  );
  const deleteUseCase = useMemo(
    () => new DeleteMasterDataDeductionTypesUseCase(repository),
    [repository],
  );
  const deleteAllUseCase = useMemo(
    () => new DeleteAllMasterDataDeductionTypesUseCase(repository),
    [repository],
  );
  const getCategoriesUseCase = useMemo(
    () =>
      new GetMasterDataDeductionCategoriesUseCase(
        new MasterDataDeductionCategoryRepository(),
      ),
    [],
  );

  useEffect(() => {
    setSelectedIds(new Set());
  }, [search, page]);

  useEffect(() => {
    if (modalMode == null && subTypeTarget == null) return;
    let cancelled = false;
    setCategoriesLoading(true);
    void getCategoriesUseCase
      .execute(
        new GetMasterDataDeductionCategoriesDTO({
          includeInactive: false,
          search: null,
        }),
      )
      .then((result) => {
        if (cancelled) return;
        setCategoryOptions(
          result.items.map((category) => ({
            categoryId: category.categoryId,
            categoryName: category.categoryName,
          })),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setCategoryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getCategoriesUseCase, modalMode, subTypeTarget]);

  const initialValues = useMemo(
    () => getDeductionTypeFormInitialValues(modalMode, editingEntity),
    [editingEntity, modalMode],
  );

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const refreshAfterWrite = useCallback(() => {
    invalidateMasterDataListCacheScopes([
      "deduction-types",
      "deduction-types-lookup",
      "deduction-subtypes-modal",
    ]);
    refetch();
  }, [refetch]);

  const handleSubmit = useCallback(
    async (values: DeductionTypeAddEditFormValues) => {
      setIsSaving(true);
      try {
        await saveMasterDataDeductionType({
          values,
          modalMode,
          editingEntity,
          createUseCase,
          updateUseCase,
        });

        successToast({ content: "Deduction type saved successfully" });
        closeModal();
        refreshAfterWrite();
      } catch (err) {
        applyDeductionTypeSaveError(
          err,
          form,
          setIsFormSubmittable,
          errorToast,
          "Failed to save deduction type. Please try again.",
          "Failed to save deduction type. Please try again later.",
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingEntity,
      errorToast,
      form,
      modalMode,
      refreshAfterWrite,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (deleteMode == null) return;
    setIsDeleting(true);
    try {
      const result =
        deleteMode === "all"
          ? await deleteAllUseCase.execute(
              new DeleteAllMasterDataDeductionTypesDTO(),
            )
          : await deleteUseCase.execute(
              new DeleteMasterDataDeductionTypesDTO({
                typeIds: Array.from(selectedIds),
              }),
            );

      notifyDeductionTypeDeleteResult(
        result,
        { singular: "type", plural: "types" },
        successToast,
        errorToast,
      );
      setSelectedIds(new Set());
      setDeleteMode(null);
      refreshAfterWrite();
    } catch (err) {
      errorToast({
        content: getMasterDataDeleteErrorMessage(
          err,
          "Failed to delete deduction types. Please try again.",
        ),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteAllUseCase,
    deleteMode,
    deleteUseCase,
    errorToast,
    refreshAfterWrite,
    selectedIds,
    successToast,
  ]);

  const selectedCount = selectedIds.size;

  return (
    <>
      <MasterDataWritableListLayout
        title="Deduction Types & Sub-Types"
        subtitle={formatMasterDataRecordCount(loading, total)}
        searchPlaceholder="Search deduction types"
        search={search}
        setSearch={setSearch}
        searchSection="types-subtypes"
        addButtonLabel="Add Deduction Type"
        onAdd={openCreateModal}
        headerActions={
          <MasterDataDeductionTypeDeleteToolbar
            selectedCount={selectedCount}
            onDeleteSelected={() => setDeleteMode("selected")}
            onDeleteAll={() => setDeleteMode("all")}
            className="ms-2"
          />
        }
        hideSearch={selectedCount > 0}
        loading={loading}
      >
        <MasterDataDeductionTypesTable
          deductionTypes={deductionTypes}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
          onViewSubTypes={setSubTypeTarget}
          onPreviewMetadata={setMetadataPreview}
          showSelection={canDelete}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </MasterDataWritableListLayout>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Deduction Type"
        editTitle="Edit Deduction Type"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataDeductionTypeAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <MasterDataDeductionSubTypesModal
        isOpen={subTypeTarget != null}
        parentTypeId={subTypeTarget?.typeId ?? null}
        parentTypeName={subTypeTarget?.typeName ?? ""}
        categoryOptions={categoryOptions}
        categoriesLoading={categoriesLoading}
        onClose={() => setSubTypeTarget(null)}
      />

      <MasterDataJsonPreviewModal
        isOpen={metadataPreview != null}
        title={
          metadataPreview
            ? `Metadata — ${metadataPreview.name}`
            : "Metadata"
        }
        value={metadataPreview?.metadata}
        onClose={() => setMetadataPreview(null)}
      />

      <MasterDataDeductionTypeDeleteConfirmModal
        deleteMode={deleteMode}
        isDeleting={isDeleting}
        selectedCount={selectedCount}
        allTitle="Delete all deduction types"
        selectedTitle="Delete selected deduction types"
        allDescription="This will delete every unlinked deduction type and sub-type, including other pages. Linked records will remain."
        entitySingular="deduction type"
        entityPlural="deduction types"
        onClose={() => setDeleteMode(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
